/**
 * auth-and-billing-logic.ts — pure (DOM-free) helpers extracted from
 * auth-and-billing.ts.
 *
 * These helpers were already commented "// ─── Pure helpers (testable without
 * DOM / Supabase)" in auth-and-billing.ts; this extraction makes that claim
 * structural — they now live in a module that has NO window/document/Supabase
 * imports, so vitest can load them in Node.js without any stubs.
 *
 * auth-and-billing.ts re-imports everything from this module and re-exports it
 * (import + re-export pattern — no compatibility shim). Byte-identical
 * extraction: logic is NOT changed, only relocated.
 *
 * WHY a separate module? Seam-based unit testing: vitest instruments the .ts
 * source via resolve.alias (vitest.config.ts spa-seam project) so coverage
 * numbers are honest. DOM + Supabase-touching code stays in auth-and-billing.ts.
 *
 * See docs/epics/active/ts-unit-testability-seams-260623/main.md and the
 * W-K wave (renderers-logic.ts) for the extraction pattern.
 */
// ─── Constants ────────────────────────────────────────────────────────────────
/**
 * Per docs/product-policy/external/customer-model-token-vs-unlimited.md §5:
 * institutional invoiced/flat groups have no per-user balance; we expose
 * `Number.MAX_SAFE_INTEGER` as a sentinel so the preflight UI says "you can
 * upload" while the server enforces the actual cap.
 */
export const UNLIMITED_SENTINEL = Number.MAX_SAFE_INTEGER;
// ─── Pure helpers (testable without DOM / Supabase) ─────────────────────────
/**
 * True iff the server marked this group unlimited (flat or invoiced), via
 * either the `is_unlimited` boolean or the `unlimited_mode` string. Works for
 * ANY group type — notably the individual (personal) group a domain grant
 * flips to invoiced-unlimited.
 */
export function isUnlimitedGroup(group) {
    if (!group)
        return false;
    if (group.is_unlimited === true)
        return true;
    const mode = group.unlimited_mode;
    if (mode === true)
        return true;
    if (typeof mode === 'string' && mode.length > 0)
        return true;
    return false;
}
/**
 * True iff the USER is unlimited — either the top-level account usage flag
 * (`usage.is_unlimited`, emitted by /api/auth/me) is set, OR any of the user's
 * groups is an unlimited group.
 *
 * This is the robust "admit every upload" predicate for the client preflight:
 * it honors unlimited status REGARDLESS of whether the group list populated.
 * The main SPA boots from /api/auth/me, which carries `usage.is_unlimited` but
 * NOT `groups[]` — so the group-aware draw chain is empty there and only the
 * usage flag survives. The prior fix (`isUnlimitedGroup` in `availableInGroup`)
 * was upstream-irrelevant because the unlimited group never reached the draw
 * chain on that path. See
 * docs/rca/purdue-institutional-upload-paywall-260824.md §"v2 addendum".
 */
export function userIsUnlimited(usage, groups) {
    if (usage?.is_unlimited === true)
        return true;
    return Array.isArray(groups) ? groups.some(isUnlimitedGroup) : false;
}
/**
 * Available credits in `group`, derived from the SERVER-EMITTED bundle fields to
 * match the canonical `decide_admit` policy (system-models/entitlement_model.py,
 * INV-ENT-4/5). Group TYPE is NOT the discriminator — unlimited-ness is
 * `is_unlimited`/`unlimited_mode`, and the per-user cap is `my_cap_credits` /
 * `my_used_credits_mtd`:
 *
 *   - Capped member (`my_cap_credits` is a number): remaining cap headroom
 *     `max(0, cap − used)`. This is the binding, needed-independent constraint the
 *     cap gate enforces BEFORE unlimited-admit (INV-ENT-4). For a finite-and-capped
 *     group the balance also bounds it, so `min(balance, headroom)`.
 *   - Uncapped UNLIMITED group (flat/invoiced, no per-user cap): UNLIMITED_SENTINEL
 *     — no finite client-side balance; the server enforces the invoice. Includes the
 *     purdue personal group a domain grant flips to invoiced-unlimited (type stays
 *     'individual'). Without this the preflight paywalls a real unlimited user (prod
 *     bug: docs/rca/purdue-institutional-upload-paywall-260824.md).
 *   - Finite, uncapped: the raw credit balance.
 *
 * Returns null when the group exposes no balance signal — caller filters these out
 * of the draw chain.
 *
 * D2 fix (Phase 5): the prior code read the NEVER-EMITTED `monthly_credits_remaining`
 * for institutional groups, so a capped institutional group over its cap wrongly
 * sentinelled → admitted. Now consumes the emitted fields per the parity corpus.
 */
export function availableInGroup(group) {
    if (!group)
        return null;
    const unlimited = isUnlimitedGroup(group);
    const cap = group.my_cap_credits;
    if (typeof cap === 'number') {
        const used = typeof group.my_used_credits_mtd === 'number' ? group.my_used_credits_mtd : 0;
        const headroom = Math.max(0, cap - used);
        if (unlimited)
            return headroom;
        const bal = typeof group.credit_balance === 'number' ? group.credit_balance : 0;
        return Math.min(bal, headroom);
    }
    if (unlimited)
        return UNLIMITED_SENTINEL;
    if (typeof group.credit_balance === 'number')
        return group.credit_balance;
    return null;
}
/**
 * Single-bucket aggregate balance (v2.1, 260509). Sums available credits
 * across every group that exposes a balance; UNLIMITED_SENTINEL short-
 * circuits the sum. Empty/null groups list → fall back to legacy
 * `fallbackUser.credit_balance`.
 */
export function computeAggregateBalance(groups, fallbackUser) {
    if (!Array.isArray(groups) || groups.length === 0) {
        const c = (fallbackUser && typeof fallbackUser.credit_balance === 'number')
            ? fallbackUser.credit_balance : null;
        return { credits: c };
    }
    let total = 0;
    let any = false;
    for (const g of groups) {
        const avail = availableInGroup(g);
        if (avail == null)
            continue;
        any = true;
        if (avail >= UNLIMITED_SENTINEL) {
            total = UNLIMITED_SENTINEL;
            break;
        }
        total += avail;
    }
    return { credits: any ? total : null };
}
/**
 * Compute draw order: institutional groups first (joined_at ASC), then
 * personal individual groups. Excludes groups with null `available`.
 *
 * Per docs/product-policy/external/customer-model-token-vs-unlimited.md §5 P-5.
 */
export function computeDrawOrder(userGroups) {
    const inst = (userGroups ?? []).filter((g) => !!g && g.type === 'institutional').slice();
    inst.sort((a, b) => String(a.joined_at ?? '').localeCompare(String(b.joined_at ?? '')));
    const personal = (userGroups ?? []).filter((g) => !!g && g.type === 'individual');
    const out = [];
    for (const g of [...inst, ...personal]) {
        const avail = availableInGroup(g);
        if (avail == null)
            continue;
        out.push({ group: g, available: avail });
    }
    return out;
}
/**
 * Sufficiency check: does the draw chain cover `needed` credits?
 * UNLIMITED_SENTINEL short-circuits true.
 */
export function canCoverWithDrawOrder(userGroups, needed) {
    const chain = computeDrawOrder(userGroups);
    let total = 0;
    for (const entry of chain) {
        if (entry.available >= UNLIMITED_SENTINEL)
            return true;
        total += entry.available;
        if (total >= needed)
            return true;
    }
    return false;
}
